import { typographyTokens } from '../../theme/typographyTokens';

import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableContainer,
  Typography,
  useTheme,
} from '@mui/material';
import { SEPSearchRow } from '../../../shared/types/SEP';
import { TaskStatus } from '../../../shared/types/Task';
import SepPhaseDetails from './SepPhaseDetails';
import EnhancedTableHead from './EnhancedTableHead';
import StatusModal from '../StatusModal/StatusModal';
import { useUpdateSepStatusMutation } from '../../services/sepSlice';
import { useAccount } from '@azure/msal-react';
import { env } from '../../../shared/env';

export interface HeadCell {
  id: string;
  key: string;
  label: string;
  visible: boolean;
  dbColumn?: string;
}

export const headCells: readonly HeadCell[] = [
  {
    id: 'id',
    key: 'id',
    label: 'Workflow Request',
    visible: true,
  },
  {
    id: 'taskProgress',
    key: '',
    label: 'Task Progress',
    visible: true,
  },
  {
    id: 'requestor',
    key: 'creator.displayName',
    label: 'Requestor',
    visible: false,
  },
  {
    id: 'created_at',
    key: 'createdAt',
    label: 'Request Date',
    visible: true,
  },
  {
    id: 'sepAge',
    key: '',
    label: 'Age (Days)',
    visible: true,
  },
  {
    id: 'status',
    key: '',
    label: 'Status',
    visible: true,
  },
  {
    id: 'notes',
    key: '',
    label: 'Notes',
    visible: true,
  },
  {
    id: 'phase',
    key: 'phase',
    label: 'Phase',
    visible: false,
  },
  {
    id: 'workFlowType',
    key: 'workFlowType',
    label: 'Workflow Type',
    visible: false,
  },
  {
    id: 'apmID',
    key: '',
    label: 'APM ID',
    visible: false, // Assuming APM ID is not always visible
  },
  {
    id: 'departmentCode',
    key: '',
    label: 'Department Code',
    visible: false, // Assuming Department Code is not always visible
  },
  {
    id: 'validatedDate',
    key: '',
    label: 'Validated Date',
    visible: false, // Assuming Validated Date is not always visible
  },
  {
    id: 'lastUpdated',
    key: '',
    label: 'Last Updated',
    visible: false, // Assuming Last Updated is not always visible
  },
  {
    id: 'closedDate',
    key: '',
    label: 'Closed Date',
    visible: false, // Assuming Closed Date is not always visible
  },
  {
    id: 'requestArea',
    key: '',
    label: 'Request Area',
    visible: false, // Assuming Request Area is not always visible
    dbColumn: 'Request Area',
  },
  {
    id: 'isNewOrExistingApplication',
    key: '',
    label: 'New/Existing Application',
    visible: false, // Assuming New/Existing Application is not always visible
    dbColumn: 'Is this an existing application or a new application?',
  },
  {
    id: 'keyTopics',
    key: '',
    label: 'Key Topics',
    visible: false, // Assuming Key Topics is not always visible
    dbColumn: 'Key Topics',
  },
  {
    id: 'businessUnit',
    key: '',
    label: 'Business Unit',
    visible: false,
    dbColumn: 'Business Unit',
  },
  {
    id: 'requestProgress',
    key: '',
    label: 'Request Progress',
    visible: true,
  }
];

const ACTIVE_TASK_STATUSES = [
  TaskStatus.todo,
  TaskStatus.inReview,
  TaskStatus.changesRequested,
];

export const getTaskCounts = (data: SEPSearchRow, userId?: string) => {
  const tasks = data.tasks || [];
  return {
    userActiveTaskCount: tasks.filter(
      (task) => task.assignedUserID === userId && ACTIVE_TASK_STATUSES.includes(task.status)
    ).length,
    userPendingTaskCount: tasks.filter(
      (task) => task.assignedUserID === userId && task.status === TaskStatus.pending
    ).length,
    // unassigned tasks are excluded, matching the previous SQL comparison against NULL
    otherTaskCount: tasks.filter(
      (task) =>
        task.assignedUserID != null &&
        task.assignedUserID !== userId &&
        ACTIVE_TASK_STATUSES.includes(task.status)
    ).length,
  };
};

export const getActiveTaskCountsByDepartment = (
  data: SEPSearchRow,
  userId: string | undefined
) => {
  const groupByDepartment = (tasks: SEPSearchRow['tasks']) =>
    tasks.reduce<Record<string, number>>((counts, task) => {
      const departmentName = task.department?.name || `Department ${task.departmentID}`;
      counts[departmentName] = (counts[departmentName] || 0) + 1;
      return counts;
    }, {});
  const activeTasks = (data.tasks || []).filter((task) =>
    ACTIVE_TASK_STATUSES.includes(task.status)
  );

  return {
    assignedToCurrentUser: groupByDepartment(
      activeTasks.filter((task) => task.assignedUserID === userId)
    ),
    assignedToOtherUsers: groupByDepartment(
      activeTasks.filter(
        (task) => task.assignedUserID != null && task.assignedUserID !== userId
      )
    ),
  };
};

export const getStatus = (data: SEPSearchRow, userId?: string) => {
  const { otherTaskCount, userActiveTaskCount, userPendingTaskCount } = getTaskCounts(data, userId);
  if (userActiveTaskCount > 0) return 'todo';
  if (userPendingTaskCount > 0) return 'pending';
  if (otherTaskCount > 0) return 'waiting';
  return 'complete';
};

export const getTaskProgress = (data: SEPSearchRow, userId?: string) => {
  const { otherTaskCount, userActiveTaskCount, userPendingTaskCount } = getTaskCounts(data, userId);

  if (window.location.href.replaceAll('/', '').slice(-8) === 'all-seps') {
    return `${
      userActiveTaskCount + userPendingTaskCount + otherTaskCount
    } tasks are available`;
  }
  if (userActiveTaskCount > 0) {
    return `You have ${userActiveTaskCount} tasks to complete`;
  }
  if (userPendingTaskCount > 0) {
    return `You have ${userPendingTaskCount} tasks pending`;
  }
  if (otherTaskCount > 0) {
    return `${otherTaskCount} tasks for others`;
  }
  return `All tasks complete`;
};

function NoSEP() {
  const [isHover, setIsHover] = useState<boolean>(false);
  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      py="36px"
    >
      <Box
        display="flex"
        alignItems="center"
        mb="20px"
        sx={{ cursor: 'pointer' }}
        onMouseOver={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
      >
        {isHover ? (
          <i
            className="fa-solid fa-plus"
            style={{ color: '#2372B9', width: '16px', fontSize: typographyTokens.fontSizes.xl }}
          ></i>
        ) : (
          <i
            className="fa-solid fa-file-magnifying-glass"
            style={{ color: '#7e8083', width: '16px', fontSize: typographyTokens.fontSizes.xl }}
          ></i>
        )}
        <Typography color="gray.solidGray" fontSize={typographyTokens.fontSizes.xl} ml="8px">
          You don’t have any SEPs
        </Typography>
      </Box>
      <Typography
        color="gray.solidGray"
        fontSize={typographyTokens.fontSizes.caption}
        maxWidth="240px"
        textAlign="center"
      >
        Choose{' '}
        <Typography
          component="span"
          color="gray.solidGray"
          fontSize={typographyTokens.fontSizes.caption}
          fontWeight={typographyTokens.fontWeights.semibold}
        >
          Create an SEP
        </Typography>{' '}
        from the navigation options to begin creating an SEP
      </Typography>
    </Box>
  );
}

const SepTableBody = ({
  rows,
  count,
  sortBy,
  setSortBy,
  sortAsc,
  setSortAsc,
  headers,
  loggedInUser,
  screenName,
  onSubscriptionChange,
  currentUserEmail,
}: {
  rows: SEPSearchRow[];
  count: number;
  sortBy: string;
  setSortBy: (sortBy: string) => void;
  sortAsc: boolean;
  setSortAsc: (sortAsc: boolean) => void;
  headers: HeadCell[];
  loggedInUser : any;
  screenName?: string;
  onSubscriptionChange?: (row: SEPSearchRow) => void;
  currentUserEmail?: string;
}) => {
  const account = useAccount();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [updateSepStatus] = useUpdateSepStatusMutation();
  const [statusAction, setStatusAction] = useState<{
    row: SEPSearchRow;
    status: 'rejected' | 'cancelled';
  } | null>(null);
  const [reason, setReason] = useState<string>('');
  const roleList = env.REACT_APP_LOCAL_DEV_ADMIN === 'true'
    ? ['AuthSuperUser']
    : account?.idTokenClaims?.roles || [];
  const isSuperUser = env.REACT_APP_LOCAL_DEV_ADMIN === 'true' || (
    roleList.includes('AuthSuperUser') &&
    (process.env.NODE_ENV === 'test'
      ? env.REACT_APP_SHOW_ADMIN_FEATURES === 'true'
      : env.REACT_APP_SHOW_ADMIN_FEATURES === 'true')
  );
  const filteredRows = loggedInUser.length >0? rows.filter((row)=>(row?.creator.id === loggedInUser) || row?.phase !== 'knockout') : rows
  const tableHeaders = isSuperUser
    ? [
        ...headers,
        { id: 'reject', key: '', label: 'Reject', visible: true },
        { id: 'cancel', key: '', label: 'Cancel', visible: true },
      ]
    : headers;
  const [detailShowed, setDetailShowed] = useState<number>(-1);
  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string
  ) => {
    const isAsc = sortBy === property && sortAsc;
    setSortAsc(!isAsc);
    setSortBy(property);
  };

  const handleStatusAction = (row: SEPSearchRow, status: 'rejected' | 'cancelled') => {
    setStatusAction({ row, status });
    setReason('');
  };

  const handleStatusModalClose = () => {
    setStatusAction(null);
    setReason('');
  };

  const handleStatusSave = async () => {
    if (!statusAction) return;

    try {
      await updateSepStatus({
        sepID: statusAction.row.id,
        status: statusAction.status,
        reason,
      }).unwrap();
      handleStatusModalClose();
    } catch {
      // The mutation displays the standard error snackbar.
    }
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {statusAction && (
        <StatusModal
          status={statusAction.status}
          open={true}
          name={statusAction.row.name || ''}
          handleStatusModalClose={handleStatusModalClose}
          handleChange={(event) => setReason(event.target.value)}
          reason={reason}
          handleStatusSave={handleStatusSave}
        />
      )}
      {count ? (
        <TableContainer
          sx={{
            flex: 1,
            minHeight: 0,
            maxWidth: '100%',
            maxHeight: 'none',
            overflow: 'auto',
            border: '1px solid',
            borderColor: theme.palette.supportingAdditional.tealContainerStroke,
            borderRadius: '6px',
            backgroundColor: isDarkMode ? theme.palette.background.default : theme.palette.content.white,
            /* width */
            '&::-webkit-scrollbar': {
              width: '12px',
              height: '12px',
            },
            /* Track */
            '&::-webkit-scrollbar-track': {
              background: theme.palette.supportingAdditional.tealUltraLight,
              borderRadius: '8px',
            },
            /* Handle */
            '&::-webkit-scrollbar-thumb': {
              background: theme.palette.prime.blue,
              border: `2px solid ${theme.palette.supportingAdditional.tealUltraLight}`,
              borderRadius: '8px',
              /* Handle on hover */
              '&:hover': {
                background: theme.palette.primeAdditional.midBlue,
              },
            },
          }}
        >
          <Table
            stickyHeader
            aria-labelledby="tableTitle"
            size="medium"
            sx={{
              backgroundColor: isDarkMode ? theme.palette.background.default : theme.palette.content.white,
              color: isDarkMode ? theme.palette.text.primary : theme.palette.content.default,
              '& .MuiTableCell-root': {
                color: isDarkMode ? theme.palette.text.primary : theme.palette.content.default,
                borderColor: theme.palette.elements.divider,
              },
              '& .MuiTableCell-stickyHeader': {
                backgroundColor: isDarkMode ? theme.palette.background.default : theme.palette.content.white,
                boxShadow: `inset 0 -1px 0 ${theme.palette.elements.divider}`,
              },
            }}
          >
            <EnhancedTableHead
              sortAsc={sortAsc}
              sortBy={sortBy}
              onRequestSort={handleRequestSort}
              headers={tableHeaders}
            />
            <TableBody>
              {filteredRows.map((row: SEPSearchRow, key: number) => {
                return (
                  <SepPhaseDetails
                    row={row}
                    key={key}
                    onDetailShow={(e) => {
                      if (e) setDetailShowed(row.id);
                      else setDetailShowed(-1);
                    }}
                    detailShow={detailShowed === row.id}
                    headers={tableHeaders}
                    screenName={screenName ? screenName : ''}
                    onSubscriptionChange={onSubscriptionChange}
                    currentUserEmail={currentUserEmail}
                    onStatusAction={handleStatusAction}
                  />
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <NoSEP />
      )}
    </Box>
  );
};

export default SepTableBody;
